import { CircularProgress, Skeleton, Typography } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import { styled } from '@mui/material/styles';
import { useCallback, useState } from 'react';
import { Document, Page as PdfPage, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const PreviewContainer = styled('div')({
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const SkeletonWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
});

const CenteredOverlay = styled(Column)({
  position: 'absolute',
  inset: 0,
  alignItems: 'center',
  justifyContent: 'center',
});

const PdfScrollContainer = styled('div')(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ImageWrapper = styled(Row)(({ theme }) => ({
  width: '100%',
  height: '100%',
  overflow: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

type Props = {
  fileUrl?: string | null;
  fileName?: string | null;
  isUploading?: boolean;
};

const InvoicePreview: React.FC<Props> = ({
  fileUrl,
  fileName,
  isUploading,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>();

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setContainerWidth(node.clientWidth);
    }
  }, []);

  if (isUploading) {
    return (
      <PreviewContainer>
        <SkeletonWrapper>
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
          />
          <CenteredOverlay spacing="sm">
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              File uploading...
            </Typography>
          </CenteredOverlay>
        </SkeletonWrapper>
      </PreviewContainer>
    );
  }

  if (!fileUrl) return null;

  const isPdf =
    fileName?.toLowerCase().endsWith('.pdf') ||
    fileUrl.toLowerCase().includes('.pdf');

  return (
    <PreviewContainer>
      {isPdf ? (
        <PdfScrollContainer ref={containerRef}>
          {containerWidth && (
            <Document
              file={fileUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            >
              {Array.from({ length: numPages ?? 0 }, (_, i) => (
                <PdfPage
                  key={i + 1}
                  pageNumber={i + 1}
                  width={containerWidth}
                />
              ))}
            </Document>
          )}
        </PdfScrollContainer>
      ) : (
        <ImageWrapper>
          <img
            src={fileUrl}
            alt="Invoice preview"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
            }}
          />
        </ImageWrapper>
      )}
    </PreviewContainer>
  );
};

export default InvoicePreview;
